import { gsap, motion, withLoader, endIntro } from "./common.js";

const root = document.querySelector(".qb-inv");
const q = (s) => root.querySelector(s);
const qa = (s) => Array.from(root.querySelectorAll(s));

const seg = q("[data-seg]");
const forms = { request: q('[data-form="request"]'), redeem: q('[data-form="redeem"]') };
const done = { request: q('[data-done="request"]'), redeem: q('[data-done="redeem"]') };
const ticket = q("[data-ticket]");
const email = q("#iv-email");
const codeInput = q("#iv-code");
let where = "laptop";

// ---------------------------------------------------------------------------
// Switching between "Request an invitation" and "I have a code"

function show(mode) {
  const redeem = mode === "redeem";
  seg.classList.toggle("r", redeem);
  qa("[data-seg] [data-mode]").forEach((b) => {
    const on = b.getAttribute("data-mode") === mode;
    b.setAttribute("aria-selected", String(on));
    b.tabIndex = on ? 0 : -1;
  });
  forms.request.hidden = redeem;
  forms.redeem.hidden = !redeem;
  done.request.hidden = true;
  done.redeem.hidden = true;
  ticket.classList.remove("is-stamped", "is-flipped");
  setTicket({ status: "Early" });
  clearError(forms.request);
  clearError(forms.redeem);
  swapIn(redeem ? forms.redeem : forms.request);
}

qa("[data-mode]").forEach((b) => b.addEventListener("click", () => show(b.getAttribute("data-mode"))));

// Where will you run it?
qa("[data-where]").forEach((b) =>
  b.addEventListener("click", () => {
    where = b.getAttribute("data-where");
    qa("[data-where]").forEach((o) => o.setAttribute("aria-pressed", String(o === b)));
  }),
);

// ---------------------------------------------------------------------------
// The ticket fills in as you type

function setTicket({ holder, code, status }) {
  if (holder !== undefined) q("[data-holder]").textContent = holder;
  if (code !== undefined) q("[data-code]").textContent = code;
  if (status !== undefined) q("[data-status]").textContent = status;
}

email.addEventListener("input", () => {
  clearError(forms.request);
  const local = email.value.split("@")[0].trim();
  setTicket({ holder: local ? (local.length > 14 ? `${local.slice(0, 13)}…` : local) : "You" });
});

/** Uppercase, keep letters and digits, and add the QB- prefix and dash. */
function formatCode(value) {
  let raw = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (raw.startsWith("QB")) raw = raw.slice(2);
  raw = raw.slice(0, 8);
  return { raw, text: raw ? `QB-${raw.slice(0, 4)}${raw.length > 4 ? `-${raw.slice(4)}` : ""}` : "" };
}

codeInput.addEventListener("input", () => {
  clearError(forms.redeem);
  const { raw, text } = formatCode(codeInput.value);
  codeInput.value = text;
  const dots = `${raw}••••••••`.slice(0, 8);
  setTicket({ code: raw ? `QB-${dots.slice(0, 4)}-${dots.slice(4)}` : "QB-••••-••••" });
});

// ---------------------------------------------------------------------------
// Submitting

function showError(form, message) {
  const box = form.querySelector(".err");
  box.querySelector("[data-err]").textContent = message;
  box.hidden = false;
  form.querySelector(".inp").classList.add("bad");
  const card = q(".iv-card");
  gsap.to(card, { duration: 0.5, ease: "none", keyframes: { x: [0, -10, 9, -7, 5, -2, 0] } });
}

function clearError(form) {
  form.querySelector(".err").hidden = true;
  form.querySelector(".inp").classList.remove("bad");
}

function busy(form, on, label) {
  const btn = form.querySelector('button[type="submit"]');
  btn.disabled = on;
  btn.querySelector("[data-label]").textContent = label;
}

async function post(path, body) {
  let res;
  try {
    res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    return { ok: false, message: "We couldn't reach Quenchbase. Check your connection and try again." };
  }
  const data = await res.json().catch(() => ({}));
  if (res.ok) return { ok: true, data };
  return { ok: false, message: data.message || "Something went wrong on our side. Try again in a minute." };
}

forms.request.addEventListener("submit", async (e) => {
  e.preventDefault();
  const form = forms.request;
  const value = email.value.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    showError(form, value ? "That email address looks incomplete. Check for a missing @ or domain." : "Enter your email so we can send your invitation.");
    email.focus();
    return;
  }
  busy(form, true, "Sending…");
  const result = await post("/api/invitations", {
    email: value,
    where,
    building: q("#iv-building").value.trim(),
    company: q("#iv-company").value,
  });
  busy(form, false, "Request an invitation");
  if (!result.ok) return showError(form, result.message);
  q("[data-sent-to]").textContent = value;
  form.hidden = true;
  done.request.hidden = false;
  ticket.classList.add("is-stamped");
  setTicket({ status: "Requested" });
  thump();
  swapIn(done.request);
});

forms.redeem.addEventListener("submit", async (e) => {
  e.preventDefault();
  const form = forms.redeem;
  const { raw, text } = formatCode(codeInput.value);
  if (raw.length !== 8) {
    showError(form, "That code looks incomplete. Codes have three parts, like QB-7K2F-9QDX. Copy it from your invitation email.");
    codeInput.focus();
    return;
  }
  busy(form, true, "Checking…");
  const result = await post("/api/invitations/redeem", { code: text });
  busy(form, false, "Redeem invitation");
  if (!result.ok) return showError(form, result.message);
  q("[data-code-out]").textContent = text;
  form.hidden = true;
  done.redeem.hidden = false;
  ticket.classList.add("is-flipped");
  setTicket({ status: "Admitted" });
  ticket.setAttribute("aria-label", "Your invitation ticket, admitted");
  swapIn(done.redeem);
});

q("[data-reset]").addEventListener("click", () => show("request"));

// ---------------------------------------------------------------------------
// Motion

function swapIn(el) {
  if (motion) gsap.fromTo(el.children, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.6, ease: "expo.out", stagger: 0.05 });
}

function thump() {
  if (motion) gsap.fromTo(".iv-tilt", { scale: 1 }, { scale: 0.965, duration: 0.12, delay: 0.32, yoyo: true, repeat: 1, ease: "power2.out" });
}

if (window.matchMedia("(hover: hover)").matches) {
  const stage = q(".iv-stage");
  const tilt = q(".iv-tilt");
  const rx = gsap.quickTo(tilt, "rotationX", { duration: 0.7, ease: "power3" });
  const ry = gsap.quickTo(tilt, "rotationY", { duration: 0.7, ease: "power3" });
  stage.addEventListener("pointermove", (e) => {
    const r = stage.getBoundingClientRect();
    ry(((e.clientX - r.left) / r.width - 0.5) * 16);
    rx(-((e.clientY - r.top) / r.height - 0.5) * 12);
  });
  stage.addEventListener("pointerleave", () => { rx(0); ry(0); });
}

if (motion && withLoader) {
  const letters = qa(".iv-loader__t span");
  gsap.set(".iv-line > span", { yPercent: 120 });
  gsap.set(".iv-in", { autoAlpha: 0, y: 24 });
  gsap.set(".iv-tilt", { yPercent: -112 });
  gsap
    .timeline({ onComplete: endIntro })
    .fromTo(letters, { yPercent: 110 }, { yPercent: 0, duration: 0.85, ease: "expo.out", stagger: 0.035 }, 0.05)
    .fromTo(".iv-loader__bar i", { scaleX: 0 }, { scaleX: 1, duration: 1.05, ease: "power2.inOut" }, 0)
    .to(letters, { yPercent: -110, duration: 0.55, ease: "power3.in", stagger: 0.02 }, 1.0)
    .to(".iv-loader", { clipPath: "inset(0% 0% 100% 0%)", duration: 0.95, ease: "expo.inOut" }, 1.25)
    .to(".iv-line > span", { yPercent: 0, duration: 1.3, ease: "expo.out", stagger: 0.08 }, 1.7)
    .to(".iv-in", { autoAlpha: 1, y: 0, duration: 1, ease: "expo.out", stagger: 0.1 }, 1.85)
    .to(".iv-tilt", { yPercent: 0, duration: 1.6, ease: "steps(16)" }, 2.1)
    .fromTo(".iv-tilt", { rotationX: -8 }, { rotationX: 0, duration: 0.9, ease: "elastic.out(1, 0.5)", immediateRender: false }, 3.7);
} else if (motion) {
  gsap
    .timeline({ defaults: { ease: "expo.out" } })
    .from(".iv-line > span", { yPercent: 120, duration: 1.2, stagger: 0.08 })
    .from(".iv-in", { autoAlpha: 0, y: 24, duration: 1, stagger: 0.1 }, 0.15)
    .from(".iv-tilt", { yPercent: -112, duration: 1.2, ease: "steps(12)" }, 0.4);
}

// Links from invitation emails: /invite?code=QB-XXXX-XXXX or /invite?mode=redeem
const params = new URLSearchParams(location.search);
const linkCode = params.get("code");
if (linkCode || params.get("mode") === "redeem") {
  show("redeem");
  if (linkCode) {
    codeInput.value = linkCode;
    codeInput.dispatchEvent(new Event("input"));
  }
}
